/**
 * What the search box's autocomplete offers at the caret (#2290), as pure
 * functions: where the caret is in the grammar (`suggestContext`), the
 * suggestions that need no lookup (`staticSuggestions`), and the text after
 * one is accepted (`applySuggestion`). Lookups (tags, people, functionals,
 * models, groups) are `useSearchSuggestions`' job.
 */
import { SORT_LABELS, WITHIN_LABELS, keyOf, lex, quote, quoteRef, readGroups, readValue, takesGroups, type EntityKind, type FilterKey } from './searchSyntax';
import { QUERY_SORTS, QUERY_WITHIN } from '@/types/modelQuery';

export type SuggestKind = 'key' | 'tag' | 'author' | 'uses' | 'submodel' | 'group' | 'date' | 'is' | 'sort';

export interface SuggestContext {
  kind: SuggestKind;
  /** What has been typed of the name or value so far, unquoted. */
  prefix: string;
  /** The range a suggestion replaces. */
  from: number;
  to: number;
  /** For a tag group, or names joined by `+` and `|` (#2299): the values already in it, not to offer again. */
  exclude: string[];
}

export interface Suggestion {
  id: string;
  label: string;
  detail?: string;
  /** Replaces the context's range. */
  insert: string;
  /** A finished token: a space follows it and the list closes. */
  complete: boolean;
  /** A person, functional, model or group picked by name: its id, so the name resolves at once. */
  learn?: { kind: EntityKind; id: string; label: string };
}

/** The filter names, in the order the help lists them. */
export const KEY_SUGGESTIONS: { insert: string; detail: string }[] = [
  { insert: 'tag:', detail: 'has a tag' },
  { insert: '-tag:', detail: 'does not have a tag' },
  { insert: 'author:', detail: 'owned by a person, or author:me' },
  { insert: 'updated:', detail: 'updated in a period or since a date' },
  { insert: 'created:', detail: 'created in a period or since a date' },
  { insert: 'is:', detail: "editor's picks, or shared with me" },
  { insert: 'uses:', detail: 'uses a functional' },
  { insert: 'submodel:', detail: 'uses a model inside it' },
  { insert: 'group:', detail: 'shared with a group' },
  { insert: 'sort:', detail: 'orders the results' },
];

const VALUE_KIND: Record<FilterKey, SuggestKind> = {
  tag: 'tag', author: 'author', uses: 'uses', submodel: 'submodel', group: 'group', updated: 'date', created: 'date', is: 'is', sort: 'sort',
};

/** The index of the last of `seps` outside quotes in `s`, or -1. */
function lastSep(s: string, seps: string): number {
  let quoted = false;
  let at = -1;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '\\' && quoted) { i++; continue; }
    if (s[i] === '"') quoted = !quoted;
    else if (seps.includes(s[i]) && !quoted) at = i;
  }
  return at;
}

/** The index of the first of `seps` at or after `from`, outside quotes, in `s`, or -1. */
function nextSep(s: string, seps: string, from: number): number {
  let quoted = false;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '\\' && quoted) { i++; continue; }
    if (s[i] === '"') quoted = !quoted;
    else if (i >= from && seps.includes(s[i]) && !quoted) return i;
  }
  return -1;
}

/** Where the caret is in the grammar, or null when there is nothing to offer. */
export function suggestContext(text: string, caret: number): SuggestContext | null {
  const lx = lex(text).find((l) => l.start < caret && caret <= l.end);
  if (!lx) return null;
  const before = lx.raw.slice(0, caret - lx.start);
  const k = keyOf(lx.raw);

  if (!k || caret - lx.start < k.valueStart) {
    if (!/^(-[A-Za-z]*|[A-Za-z]+)$/.test(before)) return null;
    // Editing a known key keeps its value: only the key is replaced.
    return { kind: 'key', prefix: before.toLowerCase(), from: lx.start, to: k ? lx.start + k.valueStart : lx.end, exclude: [] };
  }
  if (k.negated && k.key !== 'tag') return null;

  const valueStart = lx.start + k.valueStart;
  const valueBefore = text.slice(valueStart, caret);
  const valueAll = text.slice(valueStart, lx.end);
  if (k.key === 'tag' || takesGroups(k.key)) {
    // one part of a group: the part at the caret, between operators
    const seps = k.key === 'tag' ? '|' : '+|';
    const partStart = lastSep(valueBefore, seps) + 1;
    const next = nextSep(valueAll, seps, valueBefore.length);
    const partEnd = next < 0 ? valueAll.length : next;
    const { parts } = readValue(valueBefore.slice(partStart), false);
    const others = (k.key === 'tag' ? readValue(valueAll, true).parts : readGroups(valueAll).groups.flat())
      .map((p) => p.trim()).filter(Boolean);
    const prefix = parts[0] ?? '';
    return {
      kind: VALUE_KIND[k.key], prefix, from: valueStart + partStart, to: valueStart + partEnd,
      exclude: others.filter((t) => t !== prefix),
    };
  }
  const { parts } = readValue(valueBefore, false);
  return { kind: VALUE_KIND[k.key], prefix: parts[0] ?? '', from: valueStart, to: lx.end, exclude: [] };
}

const starts = (value: string, prefix: string) => value.toLowerCase().startsWith(prefix.toLowerCase());

/** The suggestions a context offers without a lookup; null when it needs one. */
export function staticSuggestions(ctx: SuggestContext): Suggestion[] | null {
  switch (ctx.kind) {
    case 'key':
      return KEY_SUGGESTIONS.filter((s) => starts(s.insert, ctx.prefix) && s.insert !== ctx.prefix)
        .map((s) => ({ id: `key:${s.insert}`, label: s.insert, detail: s.detail, insert: s.insert, complete: false }));
    case 'is':
      return [
        { id: 'is:pick', label: 'pick', detail: "Editor's picks only", insert: 'pick', complete: true },
        { id: 'is:shared', label: 'shared', detail: 'Shared with me', insert: 'shared', complete: true },
      ].filter((s) => starts(s.label, ctx.prefix));
    case 'sort':
      return QUERY_SORTS.filter((s) => starts(s, ctx.prefix))
        .map((s) => ({ id: `sort:${s}`, label: s, detail: SORT_LABELS[s], insert: s, complete: true }));
    case 'date': {
      const presets: Suggestion[] = QUERY_WITHIN.filter((w) => starts(w, ctx.prefix))
        .map((w) => ({ id: `date:${w}`, label: w, detail: WITHIN_LABELS[w], insert: w, complete: true }));
      const forms: Suggestion[] = [
        { id: 'date:>=', label: '>=', detail: 'on or after a date: >=2026-01-31', insert: '>=', complete: false },
        { id: 'date:<=', label: '<=', detail: 'on or before a date: <=2026-01-31', insert: '<=', complete: false },
      ].filter((s) => ctx.prefix === '' || (starts(s.label, ctx.prefix) && s.label !== ctx.prefix));
      return [...presets, ...forms];
    }
    default:
      return null;
  }
}

/** The lookup a context needs: its kind and text. */
export function remoteKind(ctx: SuggestContext): 'tag' | EntityKind | null {
  if (ctx.kind === 'tag') return 'tag';
  if (ctx.kind === 'author') return 'author';
  if (ctx.kind === 'uses') return 'functional';
  if (ctx.kind === 'submodel') return 'model';
  if (ctx.kind === 'group') return 'group';
  return null;
}

/** The suggestion every `author:` list starts with. */
export const ME_SUGGESTION: Suggestion = { id: 'author:me', label: 'me', detail: 'Models you own', insert: 'me', complete: true };

/** A looked-up value as a suggestion. */
export function valueSuggestion(kind: 'tag' | EntityKind, id: string, label: string, detail?: string): Suggestion {
  return kind === 'tag'
    ? { id: `tag:${label}`, label, detail, insert: quote(label), complete: true }
    : { id: `${kind}:${id}`, label, detail, insert: kind === 'functional' || kind === 'model' ? quoteRef(label) : quote(label), complete: true, learn: { kind, id: id.toLowerCase(), label } };
}

/** The text and caret after accepting a suggestion. */
export function applySuggestion(text: string, ctx: SuggestContext, s: Suggestion): { text: string; caret: number } {
  const before = text.slice(0, ctx.from);
  const after = text.slice(ctx.to);
  if (!s.complete) return { text: before + s.insert + after, caret: before.length + s.insert.length };
  const spaced = /^\s/.test(after);
  return { text: before + s.insert + (spaced ? '' : ' ') + after, caret: before.length + s.insert.length + 1 };
}
